import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { logClientEvent } from '@client/hooks/useAnalytics';
import firebaseStore from '@client/stores/firebaseStore';
import webViewStore from '@client/stores/webViewStore';

const GoogleIcon = ({ size = 16 }: { size?: number }) => {
    return (
        <svg
            aria-label="Google logo"
            width={size}
            height={size}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
        >
            <g>
                <path d="m0 0H512V512H0" fill="transparent"></path>
                <path
                    fill="#34a853"
                    d="M153 292c30 82 118 95 171 60h62v48A192 192 0 0190 341"
                ></path>
                <path fill="#4285f4" d="m386 400a140 175 0 0053-179H260v74h102q-7 37-38 57"></path>
                <path fill="#fbbc02" d="m90 341a208 200 0 010-171l63 49q-12 37 0 73"></path>
                <path
                    fill="#ea4335"
                    d="m153 219c22-69 116-109 179-50l55-54c-78-75-230-72-297 55"
                ></path>
            </g>
        </svg>
    );
};

const AppleIcon = ({ size = 16 }: { size?: number }) => {
    return (
        <svg
            aria-label="Apple logo"
            width={size}
            height={size}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1195 1195"
        >
            <path
                fill="currentColor"
                d="M1006.933 812.8c-32 153.6-115.2 211.2-147.2 249.6-32 25.6-121.6 25.6-153.6 6.4-38.4-25.6-134.4-25.6-166.4 0-44.8 32-115.2 19.2-128 12.8-256-179.2-352-716.8 12.8-774.4 64-12.8 134.4 32 134.4 32 51.2 25.6 70.4 12.8 115.2-6.4 96-44.8 243.2-44.8 313.6 76.8-147.2 96-153.6 294.4 19.2 403.2zM802.133 64c12.8 70.4-64 224-204.8 230.4-12.8-38.4 32-217.6 204.8-230.4z"
            ></path>
        </svg>
    );
};

const GoogleLoginButton = observer(({ onSuccess }: { onSuccess?: () => void }) => {
    return (
        <button
            className="btn btn-outline btn-block btn-md"
            disabled={firebaseStore.isLoggingIn}
            onClick={async () => {
                if (firebaseStore.isLoggingIn) {
                    return;
                }

                logClientEvent('login_button_click', {
                    method: 'google',
                });

                try {
                    await firebaseStore.loginWithGoogle();

                    logClientEvent('login_success', {
                        method: 'google',
                    });

                    onSuccess?.();
                } catch (error) {
                    logClientEvent('login_error', {
                        method: 'google',
                    });
                }
            }}
        >
            {firebaseStore.isLoggingIn && firebaseStore.loginMethod === 'google' ? (
                <span className="loading loading-ring loading-sm"></span>
            ) : (
                <GoogleIcon size={24} />
            )}
            구글로 로그인
        </button>
    );
});

const AppleLoginButton = observer(({ onSuccess }: { onSuccess?: () => void }) => {
    return (
        <button
            className="btn btn-outline btn-block btn-md"
            disabled={firebaseStore.isLoggingIn}
            onClick={async () => {
                if (firebaseStore.isLoggingIn) {
                    return;
                }

                logClientEvent('login_button_click', {
                    method: 'apple',
                });

                try {
                    await firebaseStore.loginWithApple();

                    logClientEvent('login_success', {
                        method: 'apple',
                    });

                    onSuccess?.();
                } catch (error) {
                    logClientEvent('login_error', {
                        method: 'apple',
                    });
                }
            }}
        >
            {firebaseStore.isLoggingIn && firebaseStore.loginMethod === 'apple' ? (
                <span className="loading loading-ring loading-sm"></span>
            ) : (
                <AppleIcon size={24} />
            )}
            애플로 로그인
        </button>
    );
});

const CodeLoginButton = observer(({ onSuccess }: { onSuccess?: () => void }) => {
    const [isInputMode, setIsInputMode] = useState(false);
    const [code, setCode] = useState('');

    const handleLogin = async () => {
        if (!code.trim()) {
            return;
        }

        if (firebaseStore.isLoggingIn) {
            return;
        }

        logClientEvent('login_button_click', {
            method: 'code',
        });

        try {
            await firebaseStore.loginWithCode(code.trim());

            logClientEvent('login_success', {
                method: 'code',
            });

            onSuccess?.();
        } catch (error) {
            logClientEvent('login_error', {
                method: 'code',
            });
        }
    };

    return (
        <div className="flex h-10.5 w-full items-center justify-center">
            {!isInputMode ? (
                <div
                    className="link-hover text-base-content/50 cursor-pointer px-5 py-2 text-xs"
                    onClick={(e) => {
                        e.preventDefault();
                        setIsInputMode(true);
                    }}
                >
                    코드로 로그인
                </div>
            ) : (
                <div className="join h-full w-full">
                    <input
                        type="text"
                        placeholder="코드 입력"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        autoFocus={true}
                        spellCheck={false}
                        inputMode="numeric"
                        className="input join-item h-full grow"
                        value={code}
                        onInput={(e) => {
                            let value = e.currentTarget.value;
                            value = value.replace(/[^0-9]/g, '');
                            setCode(value);
                        }}
                        onKeyDown={(e) => {
                            if (e.nativeEvent.isComposing) {
                                return;
                            }

                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleLogin();
                            }
                        }}
                        disabled={firebaseStore.isLoggingIn}
                    />
                    <button
                        type="button"
                        className="btn join-item h-full px-6"
                        onClick={(e) => {
                            e.preventDefault();
                            handleLogin();
                        }}
                        disabled={firebaseStore.isLoggingIn || !code.trim()}
                    >
                        {firebaseStore.isLoggingIn && firebaseStore.loginMethod === 'code' ? (
                            <span className="loading loading-ring loading-sm"></span>
                        ) : (
                            '로그인'
                        )}
                    </button>
                </div>
            )}
        </div>
    );
});

/**
 * 로그인 폼
 */
const LoginForm = observer(({ onSuccess }: { onSuccess?: () => void }) => {
    return (
        <div className="flex flex-col items-center justify-center gap-4">
            <GoogleLoginButton onSuccess={onSuccess} />
            {webViewStore.deviceInfo?.os !== 'android' && (
                <AppleLoginButton onSuccess={onSuccess} />
            )}
            <CodeLoginButton onSuccess={onSuccess} />
        </div>
    );
});

export default LoginForm;
